"use client"

import { useMemo, useState } from "react"
import { RotateCcw, Sparkles } from "lucide-react"

/**
 * GuideTool — the interactive leg of a /tools guide page (FRIDGE-586).
 *
 * The owner's bar: every /tools URL must BE a working tool, and a page that
 * was a static guide gets "a unique tool based on the topic/url". This
 * component is deliberately only the CHROME — the questions, the decision
 * logic, and the result copy are defined per page, grounded in that guide's
 * own sourced content. Fifteen guides each carry their own config; nothing
 * about the tool's substance is shared between them.
 *
 * Design constraints honored here: light backgrounds only (owner rule),
 * banded radio answers so every result is deterministic and explainable,
 * and a result that names its reasoning rather than a bare verdict.
 */

export interface GuideToolQuestion {
  id: string
  label: string
  options: { value: string; label: string }[]
}

export interface GuideToolResult {
  /** One-line verdict, e.g. "Replace the gasket". */
  verdict: string
  /** The reasoning + what to do, in plain English. */
  detail: string
  /** Colors the result card. */
  tone: "good" | "warn" | "bad"
  /** Optional next-step link into the catalog or another tool. */
  action?: { label: string; href: string }
}

export interface GuideToolProps {
  eyebrow: string
  title: string
  intro?: string
  questions: GuideToolQuestion[]
  compute: (answers: Record<string, string>) => GuideToolResult
  ctaLabel?: string
  /** Source line shown under the result, e.g. "Thresholds: USDA FSIS". */
  footnote?: string
}

const TONE_STYLES: Record<GuideToolResult["tone"], { bg: string; border: string; text: string }> = {
  good: { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534" },
  warn: { bg: "#fffbeb", border: "#fde68a", text: "#92400e" },
  bad: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b" },
}

export function GuideTool({
  eyebrow,
  title,
  intro,
  questions,
  compute,
  ctaLabel = "Get my answer",
  footnote,
}: GuideToolProps) {
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)

  const complete = questions.every((q) => answers[q.id])
  const result = useMemo(
    () => (submitted && complete ? compute(answers) : null),
    [submitted, complete, answers, compute],
  )

  const pick = (qid: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }))
    setSubmitted(false)
  }

  const reset = () => {
    setAnswers({})
    setSubmitted(false)
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden mb-12 md:mb-14">
      <div className="h-1 bg-[#ff9951]" aria-hidden="true" />
      <div className="p-6 md:p-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#ff9951] mb-1.5 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
          {eyebrow}
        </div>
        <h2
          className="font-black text-gray-900 m-0 mb-2"
          style={{ fontSize: "clamp(20px, 2.4vw, 26px)", letterSpacing: "-0.02em" }}
        >
          {title}
        </h2>
        {intro && <p className="text-[15px] text-gray-600 leading-relaxed mb-5 max-w-[640px]">{intro}</p>}

        <div className="space-y-5">
          {questions.map((q) => (
            <fieldset key={q.id} className="m-0 p-0 border-0">
              <legend className="text-[14px] font-bold text-gray-900 mb-2 p-0">{q.label}</legend>
              <div className="flex flex-wrap gap-2">
                {q.options.map((o) => {
                  const selected = answers[q.id] === o.value
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => pick(q.id, o.value)}
                      aria-pressed={selected}
                      className={[
                        "px-3.5 py-2 rounded-full text-[13.5px] font-semibold border transition-colors",
                        selected
                          ? "bg-[#0469f0] border-[#0469f0] text-white"
                          : "bg-gray-50 border-gray-200 text-gray-700 hover:border-[#0469f0]/40",
                      ].join(" ")}
                    >
                      {o.label}
                    </button>
                  )
                })}
              </div>
            </fieldset>
          ))}
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button
            type="button"
            onClick={() => setSubmitted(true)}
            disabled={!complete}
            className={[
              "px-5 py-2.5 rounded-lg text-[14px] font-bold transition-colors",
              complete
                ? "bg-[#ff9951] text-[var(--color-brand-action-text)] hover:bg-[#f08a3e]"
                // gray-500, not gray-400: the ratchet may only FALL, and a
                // disabled control still reads as unavailable at 4.84:1 —
                // cursor-not-allowed and the muted background carry that meaning.
                : "bg-gray-100 text-gray-500 cursor-not-allowed",
            ].join(" ")}
          >
            {ctaLabel}
          </button>
          {(submitted || Object.keys(answers).length > 0) && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-500 hover:text-gray-700"
            >
              <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
              Reset
            </button>
          )}
        </div>

        {result && (
          <div
            role="status"
            className="mt-6 rounded-xl border p-5"
            style={{
              background: TONE_STYLES[result.tone].bg,
              borderColor: TONE_STYLES[result.tone].border,
            }}
          >
            <div className="text-[16px] font-black mb-1" style={{ color: TONE_STYLES[result.tone].text }}>
              {result.verdict}
            </div>
            <p className="text-[14.5px] leading-relaxed text-gray-700 m-0">{result.detail}</p>
            {result.action && (
              <a
                href={result.action.href}
                className="inline-block mt-3 text-[13.5px] font-bold text-[#0469f0] hover:underline"
              >
                {result.action.label} →
              </a>
            )}
          </div>
        )}

        {/* text-gray-500 (#6a7282, 4.84:1), not gray-400 (2.60:1): this is real
            12px body text on white, so AA's 4.5:1 for normal text applies and
            no exemption does. Caught by the muted-text ratchet, 08/29/2026. */}
        {footnote && <p className="text-[12px] text-gray-500 mt-4 mb-0">{footnote}</p>}
      </div>
    </div>
  )
}
